/**
 * DefinitionCard — the next steps after a spoken glossary definition (decision 0017; owner's change of 2026-11-04).
 *
 * The voice already said the definition, so the card never repeats it. It
 * offers what the voice could not: related terms to tap, a "more" line that
 * asks the guide for the full passage, and the as-of date of a dated entry.
 * Taps go through lib/askEvents to the chat. The card never names the book.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { askQuestion } from "../../lib/askEvents";

interface Props { payload: VisualPayload }
interface Related { term: string; ask: string }
interface More { label: string; ask: string }

function Chip({ label, ask }: { label: string; ask: string }) {
  return (
    <button
      type="button"
      onClick={() => askQuestion(ask)}
      className="px-2.5 py-1 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-xs text-white/85 transition-colors"
    >
      {label}
    </button>
  );
}

export default function DefinitionCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "definition") as Record<string, unknown> | undefined;
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">Nothing more to show.</p>
      </CardShell>
    );
  }
  const related = Array.isArray(sec.related) ? (sec.related as Related[]) : [];
  const more = sec.more && typeof sec.more === "object" ? (sec.more as More) : null;
  const asOf = typeof sec.as_of === "string" && sec.dated ? sec.as_of : null;
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-3">
        {related.length > 0 && (
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">Related</p>
            <div className="flex flex-wrap gap-1.5">
              {related.map((r) => <Chip key={r.term} label={r.term} ask={r.ask} />)}
            </div>
          </div>
        )}
        {more && (
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">More</p>
            <Chip label={more.label} ask={more.ask} />
          </div>
        )}
        {asOf && <p className="text-[11px] text-white/40">As of {asOf}</p>}
      </div>
    </CardShell>
  );
}
